// D-13 — BARRA DE FILTROS COMPARTIDA por las 7 pestañas de Reportería.
//
// Este hook nació implementando RF-147 al pie de la letra ("filtros independientes por pestaña"),
// pero la maqueta —que manda para diseño y estructura (PLAN §0.1)— hace lo contrario: los paneles
// están TODOS en el DOM y `switchRepTab` solo hace show/hide, mientras la barra de filtros vive
// FUERA del panel y nunca se reinicia (`fundamentales-admin.js:1462`). El usuario que filtra por
// OU en Diario y salta a Brillamos espera seguir viendo esa OU, no un tablero en blanco.
//
// Se resuelve con D-13: barra ÚNICA compartida, y los selects propios de cada panel (BU, Depto,
// Site, Líderes/Pares del Generacional) siguen siendo locales de ese panel.
//
// Se conserva el nombre, la firma y la forma de retorno para no tocar a los consumidores, y se
// conserva `resetFilter` porque es lo que hace removible cada tag de `FilterTags` (que SÍ se
// mantiene). El parámetro `activeTab` se sigue aceptando y se IGNORA a propósito: cambiar de
// pestaña ya no reinicia nada.
import { useCallback, useState } from 'react';
import type { PersonaElegida } from '@/components/common';
import type { AnalyticsFilters } from '@/types/analytics.types';
import { DEFAULT_FILTERS } from '@/views/AnalyticsView/hooks/useAnalyticsFilters';

/**
 * Filtros de Reportería. Difieren de `AnalyticsFilters` en dos campos, y los dos por la misma razón
 * — el módulo Analytics genérico comparte ese tipo y no se toca:
 *
 *   · `ou` es una LISTA (selección múltiple, Fase 4 del plan de Reportería).
 *   · `persona` sustituye a `searchQuery`. El buscador dejó de ser texto libre para ser un combobox
 *     de selección estricta: lo que se guarda es la PERSONA elegida, y su id es lo que viaja al
 *     backend. `searchQuery` desaparece porque ya no lo lee nadie.
 */
export interface ReportFilters extends Omit<AnalyticsFilters, 'ou' | 'searchQuery'> {
  readonly ou: readonly string[];
  readonly persona: PersonaElegida | null;
}

// `DEFAULT_FILTERS` trae `searchQuery`, que `ReportFilters` ya no declara. Se listan los campos uno
// a uno (en vez de desestructurar y descartar `searchQuery`) para no dejar una variable sin usar —
// el lint del proyecto no perdona el patrón `{ searchQuery: _x, ...resto }` en variables, solo en
// parámetros de función.
const REPORT_DEFAULT_FILTERS: ReportFilters = {
  ou: [],
  bu: DEFAULT_FILTERS.bu,
  dept: DEFAULT_FILTERS.dept,
  gerencia: DEFAULT_FILTERS.gerencia,
  site: DEFAULT_FILTERS.site,
  dateFrom: DEFAULT_FILTERS.dateFrom,
  dateTo: DEFAULT_FILTERS.dateTo,
  fundFilter: DEFAULT_FILTERS.fundFilter,
  persona: null,
};

export interface UseRecognitionTabFiltersReturn {
  filters: ReportFilters;
  setFilter: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void;
  resetFilters: () => void;
  resetFilter: <K extends keyof ReportFilters>(key: K) => void;
}

export function useRecognitionTabFilters(_activeTab?: string): UseRecognitionTabFiltersReturn {
  const [filters, setFilters] = useState<ReportFilters>(REPORT_DEFAULT_FILTERS);

  const setFilter = useCallback(
    <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => setFilters(REPORT_DEFAULT_FILTERS), []);

  const resetFilter = useCallback(
    <K extends keyof ReportFilters>(key: K) => {
      setFilters((prev) => ({ ...prev, [key]: REPORT_DEFAULT_FILTERS[key] }));
    },
    [],
  );

  return { filters, setFilter, resetFilters, resetFilter };
}
